import { Button, Stack, Typography, useTheme } from '@mui/material'
import { notificationsUpdateSubscriptions } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { NotificationType } from 'api/model'
import { COLORS } from 'app-constants'
import { CheckboxWithDarkMode } from 'dark-mode-components'
import { useActiveOrganization, useFetchNotificationsListSubscriptions } from 'hooks/use-organization'
import { useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import { ReactComponent as AppIcon } from '../NavBar/app-icon-dark.svg'
import { ReactComponent as JelledText } from '../NavBar/jelled-text-dark.svg'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'

const AuthenticatedUnsubscribe = () => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const theme = useTheme()
  const user = useRecoilValue(authenticatedUserAtom)
  const isMobile = useCheckMobileScreen()

  const [hasEdits, setHasEdits] = useState<boolean>(false)

  const { notificationSubscriptions } = useFetchNotificationsListSubscriptions()

  useEffect(() => {
    const weeklyInsightsSubscription = notificationSubscriptions.find(
      (subscription) => subscription.type === NotificationType.weekly_responder_insights
    )
    if (weeklyInsightsSubscription) {
      setIsWeeklyInsightsChecked(weeklyInsightsSubscription.is_enabled)
    }

    const trendUpdatesSubscription = notificationSubscriptions.find(
      (subscription) => subscription.type === NotificationType.trend_updates
    )
    if (trendUpdatesSubscription) {
      setIsTrendUpdatesChecked(trendUpdatesSubscription.is_enabled)
    }

    if (weeklyInsightsSubscription?.is_enabled || trendUpdatesSubscription?.is_enabled) {
      setIsUnsubscribeFromAll(false)
    } else if (notificationSubscriptions.length > 0) {
      setIsUnsubscribeFromAll(true)
    }
  }, [notificationSubscriptions])

  const [isWeeklyInsightsChecked, setIsWeeklyInsightsChecked] = useState(false)
  const onCheckWeeklyInsights = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasEdits(true)
    setIsWeeklyInsightsChecked(event.target.checked)
  }

  const [isTrendUpdatesChecked, setIsTrendUpdatesChecked] = useState(false)
  const onCheckTrendUpdates = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasEdits(true)
    setIsTrendUpdatesChecked(event.target.checked)
  }

  const [isUnsubscribeFromAll, setIsUnsubscribeFromAll] = useState(false)

  const onCheckUnsubscribeFromAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasEdits(true)
    if (event.target.checked) {
      setIsTrendUpdatesChecked(false)
      setIsWeeklyInsightsChecked(false)
    }
    setIsUnsubscribeFromAll(event.target.checked)
  }

  const onUpdateSubscription = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      // get just the subscriptions that were updated
      const subscriptionsToUpdate = []
      if (isWeeklyInsightsChecked !== notificationSubscriptions[0].is_enabled) {
        subscriptionsToUpdate.push({
          type: NotificationType.weekly_responder_insights,
          is_enabled: isWeeklyInsightsChecked,
        })
      }
      if (isTrendUpdatesChecked !== notificationSubscriptions[1].is_enabled) {
        subscriptionsToUpdate.push({
          type: NotificationType.trend_updates,
          is_enabled: isTrendUpdatesChecked,
        })
      }

      await notificationsUpdateSubscriptions(activeOrganization, subscriptionsToUpdate)
      // set success toast
      setToast({ title: 'Successfully updated email notifications', severity: 'success' })
      setHasEdits(false)
    } catch (error) {
      setToast({ title: 'Failed to update email notification failed', severity: 'error' })
    }
  }

  return (
    <Stack
      sx={{
        background: COLORS.PURE_WHITE,
        justifyContent: 'center',
        alignItems: 'center',
        height: isMobile ? 'inherit' : '100vh',
        width: '100vw',
        zIndex: 9999,
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        px: isMobile ? 2 : 0,
      }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={5}>
        <AppIcon />

        <JelledText
          style={{
            width: '85px',
            height: '30px',
          }}
        />
      </Stack>
      <Stack
        borderRadius="20px"
        spacing={5}
        width={isMobile ? '100%' : '610px'}
        justifyContent="center"
        alignItems="center">
        <Typography
          variant="h3"
          sx={{
            color: COLORS.GRAY_1,
          }}>
          Emails
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: COLORS.GRAY_2,
          }}>
          Showing preferences for {user?.email}
        </Typography>

        <Stack direction="row" justifyContent="flex-start" width="100%" my="10px">
          <CheckboxWithDarkMode
            disableRipple
            disabled={isUnsubscribeFromAll}
            theme={theme}
            checked={isWeeklyInsightsChecked}
            onChange={onCheckWeeklyInsights}
            color="primary"
          />
          <Stack>
            <Typography
              variant="pS"
              sx={{
                color: isUnsubscribeFromAll ? COLORS.GRAY_3 : COLORS.GRAY_1,
              }}>
              Weekly insights
            </Typography>
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_2,
              }}>
              A weekly report of your cross-platform insights, statistics, tips
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-start" width="100%" my="10px">
          <CheckboxWithDarkMode
            disableRipple
            theme={theme}
            disabled={isUnsubscribeFromAll}
            checked={isTrendUpdatesChecked}
            onChange={onCheckTrendUpdates}
            color="primary"
          />
          <Stack>
            <Typography
              variant="pS"
              sx={{
                color: isUnsubscribeFromAll ? COLORS.GRAY_3 : COLORS.GRAY_1,
              }}>
              Trend updates (maximum of once a day)
            </Typography>
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_2,
              }}>
              Get an update when your digital twin detects an update to an existing trend
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-start" width="100%" my="10px">
          <CheckboxWithDarkMode
            theme={theme}
            checked={isUnsubscribeFromAll}
            onChange={onCheckUnsubscribeFromAll}
            color="primary"
            disableRipple
          />
          <Stack>
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_1,
              }}>
              Unsubscribe from all email lists
            </Typography>
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_2,
              }}>
              You will still receive important transactional and billing-related emails
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Button
        variant="contained"
        color="primary"
        size="medium"
        disabled={!hasEdits}
        onClick={onUpdateSubscription}
        sx={{
          marginTop: '32px',
        }}>
        Update preferences
      </Button>
    </Stack>
  )
}

export default AuthenticatedUnsubscribe
